/**
 * 리뷰 화면의 "수정" 이 무엇을 여는가 — 선언 테이블.
 *
 * 레거시에서는 `CounselStartPage.handleEditIntakeSection` 안의 `subStageMap` 이 이 일을
 * 했다. 리뷰 카드가 자기가 무엇을 여는지 모르고, 페이지가 대신 알고 있는 구조였다. 카드를
 * 하나 추가하려면 화면과 페이지 두 곳을 고쳐야 했다.
 *
 * 이제 라우팅 대상과 그 화면이 필요로 하는 prefill 이 한 곳에 선언된다. 리뷰 문서의
 * `TextAction` 은 여기 정의된 route 이름 하나만 가리키면 된다.
 *
 * ## 왜 sub-stage 이름을 그대로 두었나
 *
 * `moving-date`, `arrival-address` 같은 이름은 stage 머신(`counselStageMachine`)이 이미
 * 쓰는 어휘다. 새 이름을 만들면 두 어휘를 대조해야 하므로 그대로 쓴다.
 */

import type { DataSource } from '@miso/design-system';
import { ARRIVAL_ADDRESS_PREFILL_DATA, transportPrefillData } from '../data/prefill';

/** 리뷰 카드에서 수정할 수 있는 영역. 레거시 `onEditIntakeSection` 의 인자와 같다. */
export type ReviewSection =
  | 'date'
  | 'departure'
  | 'arrival'
  | 'packing'
  | 'movingType'
  | 'customer';

export interface ReviewEditRoute {
  /** 열릴 progress sub-stage. */
  sub: string;
  /** 그 화면이 열릴 때 채워질 값. 없으면 빈 폼. */
  data?: DataSource[];
  /** 왜 이 sub-stage 인지 — 1:1 이 아닌 경우에만. */
  note?: string;
}

/**
 * 영역 → 열리는 화면.
 *
 * `packing` 이 `departure-transport` 로 가는 것은 오타가 아니다. 포장방식은 출발지/도착지
 * 카드 안에 표시되므로 운반방식부터 시작해 자연스럽게 포장 단계까지 흐르게 한 것이다.
 * 레거시 주석에 있던 이 의도를 선언에 남긴다.
 */
export const REVIEW_EDIT_ROUTES: Record<ReviewSection, ReviewEditRoute> = {
  date: { sub: 'moving-date' },
  departure: {
    sub: 'departure-transport',
    data: transportPrefillData('departure'),
  },
  arrival: {
    sub: 'arrival-address',
    data: ARRIVAL_ADDRESS_PREFILL_DATA,
  },
  packing: {
    sub: 'departure-transport',
    data: transportPrefillData('departure'),
    note: '포장은 출발지 카드 안에 있어 운반방식부터 순차 진행한다',
  },
  movingType: { sub: 'moving-types' },
  customer: { sub: 'customer-requests' },
};

/**
 * 레거시 `subStageMap` 과 같은 모양 — 페이지가 그대로 쓸 수 있게.
 *
 * 페이지와 선언이 같은 테이블을 보게 해서 둘이 갈라지는 것을 막는다.
 */
export function reviewEditSubStage(section: ReviewSection): { kind: string } {
  return { kind: REVIEW_EDIT_ROUTES[section].sub };
}
